import React, { useState } from 'react'
import type { LocationDetail } from '../../../../../types/location'
import { getCountryName, getStateName } from '../../../constants/shippingZones'
import { formatTaxPercent, zoneKey } from '../../../utils/shippingTax'
import type { LocationTab, LocationTabIntent } from '../LocationTabBar'
import { EditShippingTaxDialog } from '../dialogs/EditShippingTaxDialog'
import { ZoneTaxCell, customTaxPillStyle } from './ShippingZoneRow'

interface Props {
  location: LocationDetail
  onTabChange: (tab: LocationTab, intent?: LocationTabIntent) => void
}

const cardStyle: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #E5E7EB',
  borderRadius: 12,
  padding: 20,
}

const editBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '6px 14px', fontSize: 12.5, fontWeight: 600,
  color: '#374151', background: '#F9FAFB', border: '1px solid #E5E7EB',
  borderRadius: 8, cursor: 'pointer', flexShrink: 0,
}

const linkBtnStyle: React.CSSProperties = {
  padding: 0, border: 'none', background: 'none',
  fontSize: 12.5, fontWeight: 600, color: '#2563EB', cursor: 'pointer',
  fontFamily: 'inherit',
}

const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '2fr 1.6fr 1.4fr 64px',
  gap: 8,
  alignItems: 'center',
  padding: '10px 14px',
  fontSize: 13,
  color: '#2C3E50',
  borderTop: '1px solid #F1F5F9',
}

const headerGridStyle: React.CSSProperties = {
  ...gridStyle,
  borderTop: 'none',
  fontSize: 11,
  fontWeight: 600,
  color: '#94A3B8',
  textTransform: 'uppercase',
  letterSpacing: '.06em',
  background: '#FAFBFC',
}

const pageBtnStyle = (disabled: boolean): React.CSSProperties => ({
  padding: '4px 10px', fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
  color: disabled ? '#CBD5E1' : '#374151', background: '#fff',
  border: '1px solid #E5E7EB', borderRadius: 6,
  cursor: disabled ? 'not-allowed' : 'pointer',
})

const ZONES_PER_PAGE = 5

const PercentIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0E7490" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="5" x2="5" y2="19" />
    <circle cx="6.5" cy="6.5" r="2.5" />
    <circle cx="17.5" cy="17.5" r="2.5" />
  </svg>
)

export const ShippingTaxCard: React.FC<Props> = ({ location, onTabChange }) => {
  const [editOpen, setEditOpen] = useState(false)
  const { shippingTaxPercentage, mailOrderZones, mailOrderEnabled } = location
  const customCount = mailOrderZones.filter(
    (z) => z.shipping_tax_percentage != null && z.shipping_tax_percentage !== '',
  ).length

  const [page, setPage] = useState(0)
  const pageCount = Math.max(1, Math.ceil(mailOrderZones.length / ZONES_PER_PAGE))
  // Clamp when zones are removed while viewing a later page.
  const currentPage = Math.min(page, pageCount - 1)
  const pageStart = currentPage * ZONES_PER_PAGE
  const pagedZones = mailOrderZones.slice(pageStart, pageStart + ZONES_PER_PAGE)

  const openZone = (key?: string) =>
    onTabChange('mail-order', {
      mailOrder: { focus: 'shipping-zones', editZoneKey: key },
    })

  return (
    <>
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 8, background: '#CFFAFE',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <PercentIcon />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <p style={{ fontSize: 13.5, fontWeight: 700, color: '#111827', margin: 0 }}>
                  Shipping Tax
                </p>
                {customCount > 0 && <span style={customTaxPillStyle}>{customCount} custom</span>}
              </div>
              <p style={{ fontSize: 12, color: '#6B7280', margin: '2px 0 0' }}>
                Applied to delivery and mail order shipping fees — not pickup, which has no shipping fee.
              </p>
            </div>
          </div>
          <button type="button" onClick={() => setEditOpen(true)} style={editBtnStyle}>
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.6">
              <path d="M8.5 2L11 4.5 4.5 11H2V8.5L8.5 2z" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Edit Default
          </button>
        </div>

        <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #F3F4F6' }}>
          <p style={{ fontSize: 12.5, color: '#6B7280', margin: 0 }}>
            Default rate{' '}
            <span style={{ fontWeight: 600, color: '#111827' }}>{formatTaxPercent(shippingTaxPercentage)}</span>
          </p>
          <p style={{ fontSize: 12, color: '#94A3B8', margin: '2px 0 0' }}>
            Applies to delivery and mail order. Mail order zones can override it with a custom rate.
          </p>
          {!mailOrderEnabled && (
            <p style={{ fontSize: 12, color: '#B45309', margin: '8px 0 0' }}>
              Mail order is currently off for this location — zone overrides apply once it's enabled.
            </p>
          )}
        </div>

        <div style={{ marginTop: 16 }}>
          <p style={{ fontSize: 12.5, fontWeight: 600, color: '#111827', margin: 0 }}>
            Mail order zone overrides
          </p>
          <p style={{ fontSize: 12, color: '#94A3B8', margin: '2px 0 0' }}>
            Only mail order supports a custom rate per zone. Zones without one use the default rate.
          </p>
        </div>

        {mailOrderZones.length === 0 ? (
          <div style={{
            marginTop: 10, padding: 14, border: '2px dashed #E8ECF0', borderRadius: 8,
            fontSize: 12.5, color: '#7F8C8D', display: 'flex', justifyContent: 'space-between', gap: 12,
          }}>
            <span>No mail order zones yet — mail orders use the default rate.</span>
            <button type="button" onClick={() => openZone()} style={linkBtnStyle}>
              Add zone →
            </button>
          </div>
        ) : (
          <div style={{ marginTop: 10, border: '1px solid #E8ECF0', borderRadius: 8, overflow: 'hidden' }}>
            <div style={headerGridStyle}>
              <div>Zone</div>
              <div>Region</div>
              <div>Effective Tax</div>
              <div />
            </div>
            {pagedZones.map((zone) => {
              const key = zoneKey(zone)
              const stateName = getStateName(zone.country, zone.state)
              return (
                <div key={key} style={gridStyle}>
                  <div style={{ fontWeight: 500 }}>{zone.name || stateName}</div>
                  <div style={{ fontSize: 12, color: '#5A6B7B' }}>
                    {stateName}, {getCountryName(zone.country)}
                  </div>
                  <div>
                    <ZoneTaxCell zone={zone} defaultTax={shippingTaxPercentage} />
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <button
                      type="button"
                      onClick={() => openZone(key)}
                      style={linkBtnStyle}
                      aria-label={`Edit shipping tax for ${zone.name || stateName}`}
                    >
                      Edit
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {mailOrderZones.length > 0 && (
          <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            {pageCount > 1 ? (
              <nav aria-label="Shipping zones pagination" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => setPage(currentPage - 1)}
                  disabled={currentPage === 0}
                  style={pageBtnStyle(currentPage === 0)}
                >
                  ← Back
                </button>
                <span style={{ fontSize: 12, color: '#6B7280' }}>
                  {pageStart + 1}–{pageStart + pagedZones.length} of {mailOrderZones.length}
                </span>
                <button
                  type="button"
                  onClick={() => setPage(currentPage + 1)}
                  disabled={currentPage >= pageCount - 1}
                  style={pageBtnStyle(currentPage >= pageCount - 1)}
                >
                  Next →
                </button>
              </nav>
            ) : (
              <span />
            )}
            <button type="button" onClick={() => openZone()} style={linkBtnStyle}>
              Manage shipping zones →
            </button>
          </div>
        )}
      </div>

      <EditShippingTaxDialog open={editOpen} location={location} onClose={() => setEditOpen(false)} />
    </>
  )
}
